import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  cislo,
  cssPremenna,
  formatujSumu,
  useTemaGrafu,
  vytvorObrazokGrafu,
} from "./investGrafPomocne";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

function formatujPercentoGraf(hodnota) {
  return `${hodnota.toFixed(1).replace(".", ",")} %`;
}

function formatujInflaciu(hodnota) {
  return `${cislo(hodnota).toFixed(2).replace(".", ",")} % p.a.`;
}

const MINIMALNY_VIZUALNY_PODIEL_GRAFU = 3;

function upravPercentaPreGraf(percenta) {
  const hodnoty = percenta.map((hodnota) => Math.max(0, cislo(hodnota)));
  const sucet = hodnoty.reduce((spolu, hodnota) => spolu + hodnota, 0);

  if (sucet <= 0) return hodnoty.map(() => 0);

  const normalizovane = hodnoty.map((hodnota) => (hodnota / sucet) * 100);
  const nenuloveIndexy = normalizovane
    .map((hodnota, index) => ({ hodnota, index }))
    .filter((polozka) => polozka.hodnota > 0);

  if (nenuloveIndexy.length <= 1) return normalizovane;

  const minimalnyPodiel = Math.min(
    MINIMALNY_VIZUALNY_PODIEL_GRAFU,
    (100 / nenuloveIndexy.length) * 0.65
  );

  const maleIndexy = nenuloveIndexy.filter((polozka) => polozka.hodnota < minimalnyPodiel);
  if (!maleIndexy.length) return normalizovane;

  const kapacitaVelkych = nenuloveIndexy.reduce((sucetKapacity, polozka) => (
    polozka.hodnota > minimalnyPodiel
      ? sucetKapacity + (polozka.hodnota - minimalnyPodiel)
      : sucetKapacity
  ), 0);

  const potrebneNavysenie = maleIndexy.reduce((sucetNavysenia, polozka) => (
    sucetNavysenia + (minimalnyPodiel - polozka.hodnota)
  ), 0);

  if (kapacitaVelkych <= 0 || potrebneNavysenie <= 0) return normalizovane;

  const skutocneNavysenie = Math.min(potrebneNavysenie, kapacitaVelkych);
  const pomerNavysenia = skutocneNavysenie / potrebneNavysenie;
  const upravene = [...normalizovane];

  maleIndexy.forEach((polozka) => {
    upravene[polozka.index] += (minimalnyPodiel - polozka.hodnota) * pomerNavysenia;
  });

  nenuloveIndexy.forEach((polozka) => {
    if (polozka.hodnota <= minimalnyPodiel) return;

    const kapacita = polozka.hodnota - minimalnyPodiel;
    upravene[polozka.index] -= (kapacita / kapacitaVelkych) * skutocneNavysenie;
  });

  const sucetUpravenych = upravene.reduce((spolu, hodnota) => spolu + hodnota, 0);

  return sucetUpravenych > 0
    ? upravene.map((hodnota) => (hodnota / sucetUpravenych) * 100)
    : normalizovane;
}

const SEGMENT_LABEL_STYL =
  Object.freeze({
    obrazovka: Object.freeze({
      nadpis: 10,
      hodnota: 9,
      vyskaRiadka: 11,
    }),
    pdf: Object.freeze({
      nadpis: 12,
      hodnota: 11,
      vyskaRiadka: 13,
    }),
  });

const segmentLabelPlugin = {
  id: "segmentLabelPlugin",

  afterDatasetsDraw(chart) {
    const { ctx } = chart;

    const textFarba =
      cssPremenna(
        "--text",
        "#f3f3f3"
      );

    const styl =
      chart.$investPdfRezim
        ? SEGMENT_LABEL_STYL.pdf
        : SEGMENT_LABEL_STYL
            .obrazovka;

    chart.data.datasets.forEach(
      (
        dataset,
        datasetIndex
      ) => {
        const meta =
          chart.getDatasetMeta(
            datasetIndex
          );

        meta.data.forEach(
          (bar, index) => {
            const raw =
              Number(
                chart.config.data
                  ?.datasets?.[
                    datasetIndex
                  ]?.realValues?.[
                    index
                  ]
              ) || 0;

            const realPercento =
              Number(
                chart.config.data
                  ?.datasets?.[
                    datasetIndex
                  ]
                  ?.realPercentValues?.[
                    index
                  ]
              );

            const percento =
              Number(
                dataset.data?.[
                  index
                ]
              ) || 0;

            const percentoPreText =
              Number.isFinite(
                realPercento
              )
                ? realPercento
                : percento;

            if (
              raw <= 0 ||
              percento < 3
            ) {
              return;
            }

            const {
              x,
              y,
              base,
            } = bar;

            const left =
              Math.min(x, base);

            const right =
              Math.max(x, base);

            const sirka =
              right - left;

            if (sirka < 58) {
              return;
            }

            const nazovSegmentu =
              dataset.label ===
              "Čistý výnos"
                ? "VÝNOS"
                : String(
                    dataset.label ||
                      ""
                  ).toUpperCase();

            const riadky =
              sirka >= 118
                ? [
                    nazovSegmentu,
                    formatujSumu(
                      raw
                    ),
                    formatujPercentoGraf(
                      percentoPreText
                    ),
                  ]
                : [
                    nazovSegmentu,
                    formatujPercentoGraf(
                      percentoPreText
                    ),
                  ];

            const startY =
              y -
              (
                (
                  riadky.length -
                  1
                ) *
                styl.vyskaRiadka
              ) /
                2;

            ctx.save();

            ctx.fillStyle =
              textFarba;

            ctx.textAlign =
              "center";

            ctx.textBaseline =
              "middle";

            riadky.forEach(
              (
                riadok,
                riadokIndex
              ) => {
                const velkostPisma =
                  riadokIndex === 0
                    ? styl.nadpis
                    : styl.hodnota;

                const hrubkaPisma =
                  riadokIndex === 0
                    ? 700
                    : 600;

                ctx.font =
                  `${hrubkaPisma} ` +
                  `${velkostPisma}px ` +
                  "Convergence, " +
                  "system-ui, " +
                  "sans-serif";

                ctx.fillText(
                  riadok,
                  left +
                    sirka / 2,
                  startY +
                    riadokIndex *
                      styl.vyskaRiadka
                );
              }
            );

            ctx.restore();
          }
        );
      }
    );
  },
};

const InvesticneZlozenieGrafKarta =
  React.forwardRef(
    function InvesticneZlozenieGrafKarta(
      {
        vysledok,
        vysledkyInvesticii,
        porovnanieInvesticii,
      },
      ref
    ) {
  const maPorovnanie = Boolean(porovnanieInvesticii && vysledkyInvesticii?.variant2);
  const temaGrafu = useTemaGrafu();

  const chartRef =
    React.useRef(null);

  const surfaceFarba =
    cssPremenna(
      "--surface",
      "#ffffff"
    );

  const varianty = maPorovnanie
    ? [
        { key: "variant1", label: "Variant 1", vysledok: vysledkyInvesticii?.variant1 || vysledok },
        { key: "variant2", label: "Variant 2", vysledok: vysledkyInvesticii?.variant2 },
      ]
    : [
        { key: "variant1", label: "Variant 1", vysledok },
      ];

  const hodnoty = varianty.map((variant) => {
    const data = variant.vysledok || {};
    const vklady = Math.max(0, cislo(data.nakladyKlienta ?? data.celkoveVklady));
    const cistyVynos = Math.max(0, cislo(data.celkovyVynos));
    const dan = Math.max(0, cislo(data.danZVynosu));
    const poplatky = Math.max(0, cislo(data.celkovePoplatky));
    const celkomGraf = vklady + cistyVynos + dan;

    const naPercenta = (hodnota) => (
      celkomGraf > 0
        ? (Math.max(0, cislo(hodnota)) / celkomGraf) * 100
        : 0
    );

    const realnePercenta = [
      naPercenta(vklady),
      naPercenta(cistyVynos),
      naPercenta(dan),
    ];

    const vizualnePercenta = upravPercentaPreGraf(realnePercenta);

    return {
      ...variant,
      vklady,
      cistyVynos,
      dan,
      poplatky,
      celkomGraf,

      vkladyPercento: vizualnePercenta[0],
      cistyVynosPercento: vizualnePercenta[1],
      danPercento: vizualnePercenta[2],

      vkladyRealPercento: realnePercenta[0],
      cistyVynosRealPercento: realnePercenta[1],
      danRealPercento: realnePercenta[2],

      hodnotaInvesticie: Math.max(0, cislo(data.konecnaHodnota)),
      realnaHodnotaPoInflacii: Math.max(0, cislo(data.realnaHodnotaPoInflacii)),
      inflacia: cislo(data.inflacia),
    };
  });

  const maData = hodnoty.some((variant) => variant.celkomGraf > 0);

  const data = {
    labels: hodnoty.map((variant) => variant.label),
    datasets: [
      {
        label: "Vklady",
        data: hodnoty.map((variant) => variant.vkladyPercento),
        realValues: hodnoty.map((variant) => variant.vklady),
        realPercentValues: hodnoty.map((variant) => variant.vkladyRealPercento),
        backgroundColor: cssPremenna("--invest-graf-vklady", "rgba(100, 171, 210, 0.92)"),
        borderColor: cssPremenna("--surface", "#1f1f1f"),
        borderWidth: 1,
        stack: "vysledok",
      },
      {
        label: "Čistý výnos",
        data: hodnoty.map((variant) => variant.cistyVynosPercento),
        realValues: hodnoty.map((variant) => variant.cistyVynos),
        realPercentValues: hodnoty.map((variant) => variant.cistyVynosRealPercento),
        backgroundColor: cssPremenna("--invest-graf-vynos", "rgba(156, 139, 96, 0.92)"),
        borderColor: cssPremenna("--surface", "#1f1f1f"),
        borderWidth: 1,
        stack: "vysledok",
      },
      {
        label: "Daň",
        data: hodnoty.map((variant) => variant.danPercento),
        realValues: hodnoty.map((variant) => variant.dan),
        realPercentValues: hodnoty.map((variant) => variant.danRealPercento),
        backgroundColor: cssPremenna("--invest-graf-dan", "rgba(205, 86, 67, 0.82)"),
        borderColor: cssPremenna("--surface", "#1f1f1f"),
        borderWidth: 1,
        stack: "vysledok",
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        max: 100,
        ticks: {
          color: cssPremenna("--muted", "rgba(255,255,255,0.68)"),
          callback: (value) => `${value} %`,
          font: {
            size: 10,
            weight: "600",
          },
        },
        grid: {
          color: cssPremenna("--line", "rgba(255,255,255,0.12)"),
        },
        border: {
          color: cssPremenna("--line", "rgba(255,255,255,0.12)"),
        },
      },
      y: {
        stacked: true,
        ticks: {
          color: cssPremenna("--text", "#f3f3f3"),
          font: {
            size: 11,
            weight: "700",
          },
        },
        grid: {
          display: false,
        },
        border: {
          color: cssPremenna("--line", "rgba(255,255,255,0.12)"),
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: cssPremenna("--text", "#f3f3f3"),
          boxWidth: 10,
          boxHeight: 10,
          padding: 12,
          font: {
            size: 11,
            weight: "600",
          },
        },
      },
      tooltip: {
        backgroundColor: cssPremenna("--surface", "#1f1f1f"),
        titleColor: cssPremenna("--text", "#f3f3f3"),
        bodyColor: cssPremenna("--text", "#f3f3f3"),
        borderColor: cssPremenna("--line", "rgba(255,255,255,0.12)"),
        borderWidth: 1,
        callbacks: {
          label: (ctx) => {
            const hodnota = Number(ctx.dataset.realValues?.[ctx.dataIndex]) || 0;
            const realPercento = Number(ctx.dataset.realPercentValues?.[ctx.dataIndex]);
            const percento = Number.isFinite(realPercento)
              ? realPercento
              : Number(ctx.raw) || 0;

            return `${ctx.dataset.label}: ${formatujSumu(hodnota)} (${formatujPercentoGraf(percento)})`;
          },
          afterBody: (ctx) => {
            const index = ctx?.[0]?.dataIndex;
            const variant = hodnoty[index];

            if (!variant) return [];

            return [
              `Celková hodnota investície: ${formatujSumu(variant.hodnotaInvesticie)}`,
              `Reálne po inflácii (${formatujInflaciu(variant.inflacia)}): ${formatujSumu(variant.realnaHodnotaPoInflacii)}`,
              `Poplatky: ${formatujSumu(variant.poplatky)}`,
            ];
          },
        },
      },
    },
  };

  React.useImperativeHandle(
    ref,
    () => ({
      ziskajPdfData: () => ({
        obrazok: maData
          ? vytvorObrazokGrafu(
              chartRef.current,
              surfaceFarba,
              {
                pdfRezim: true,
              }
            )
          : null,

        poznamky: hodnoty.map(
          (variant) => ({
            label: variant.label,

            riadky: [
              {
                text:
                  "Celková hodnota investície:",
                zvyraznit: false,
              },
              {
                text: formatujSumu(
                  variant
                    .hodnotaInvesticie
                ),
                zvyraznit: true,
              },
            ],
          })
        ),
      }),
    }),
    [
      maData,
      surfaceFarba,
      hodnoty,
    ]
  );

  return maData ? (
    <>
      <div
        className={`invest-stacked-chart-wrap ${
          maPorovnanie
            ? "is-compare"
            : ""
        }`}
      >
        <Bar
          ref={chartRef}
          key={temaGrafu}
          data={data}
          options={options}
          plugins={[segmentLabelPlugin]}
        />
      </div>

      <div className="invest-graf-realne">
        {hodnoty.map((variant) => (
          <div
            key={variant.key}
            className="invest-graf-realne-row"
          >
            <span>{variant.label}</span>

            <strong>
              Celková hodnota investície:{" "}
              {formatujSumu(
                variant.hodnotaInvesticie
              )}
            </strong>
          </div>
        ))}
      </div>
    </>
  ) : (
    <div className="invest-graf-empty">
      Zadaj vstupné hodnoty pre
      zobrazenie grafu.
    </div>
  );
}
  );

export default
  InvesticneZlozenieGrafKarta;